import React, { useEffect, useMemo, useState } from "react";
import { FaCopy, FaExternalLinkAlt, FaFilePdf, FaTrash, FaUpload } from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import { supabase } from "../../../supabaseClient";
import { formatProfileName } from "../../../utils/profileName";

const PUBLIC_BASE_URL = "https://ruht.cloud/documents";
const MAX_FILE_BYTES = 10 * 1024 * 1024;

function cleanSlug(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\.pdf$/i, "")
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^[-_]+|[-_]+$/g, "")
    .slice(0, 120);
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!value) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ManagementDocuments({ session, profile }) {
  const [documents, setDocuments] = useState([]);
  const [fileName, setFileName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");

  const loadDocuments = async () => {
    const { data, error } = await supabase
      .from("documents")
      .select("id,file_name,display_name,storage_path,file_size,mime_type,uploaded_at,uploaded_by,profiles:uploaded_by(full_name,name_prefix)")
      .eq("active", true)
      .order("uploaded_at", { ascending: false });
    if (error) {
      setMessage(error.message || "Unable to load documents.");
      return;
    }
    setDocuments(data || []);
  };

  useEffect(() => { void loadDocuments(); }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((doc) => [doc.display_name, doc.file_name, doc.storage_path].filter(Boolean).some((value) => String(value).toLowerCase().includes(q)));
  }, [documents, search]);

  const upload = async (event) => {
    event.preventDefault();
    setMessage("");
    const slug = cleanSlug(fileName || file?.name || "");
    if (!slug) { setMessage("Enter a valid file name."); return; }
    if (!file) { setMessage("Choose a PDF document to upload."); return; }
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) { setMessage("Only PDF documents can be uploaded."); return; }
    if (file.size > MAX_FILE_BYTES) { setMessage("PDFs must be 10 MB or smaller."); return; }

    const storagePath = `${slug}.pdf`;
    setBusy(true);
    try {
      const { data: existing } = await supabase.from("documents").select("id").eq("storage_path", storagePath).maybeSingle();
      if (existing?.id) throw new Error("That public file name is already in use. Choose another name or delete the existing document first.");

      const { error: uploadError } = await supabase.storage.from("documents").upload(storagePath, file, {
        cacheControl: "3600",
        contentType: "application/pdf",
        upsert: false,
      });
      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase.from("documents").insert({
        file_name: storagePath,
        display_name: displayName.trim() || fileName.trim() || slug,
        storage_path: storagePath,
        uploaded_by: session?.user?.id || null,
        file_size: file.size,
        mime_type: "application/pdf",
        active: true,
      });
      if (dbError) {
        await supabase.storage.from("documents").remove([storagePath]);
        throw dbError;
      }

      setFileName("");
      setDisplayName("");
      setFile(null);
      const input = document.getElementById("ruht-document-file");
      if (input) input.value = "";
      setMessage(`Document uploaded. Public URL: ${PUBLIC_BASE_URL}/${storagePath}`);
      await loadDocuments();
    } catch (error) {
      setMessage(error?.message || "Unable to upload this document.");
    } finally {
      setBusy(false);
    }
  };

  const removeDocument = async (doc) => {
    if (!window.confirm(`Delete ${doc.display_name || doc.file_name}? The public link will stop working.`)) return;
    setBusy(true);
    setMessage("");
    try {
      const { error: storageError } = await supabase.storage.from("documents").remove([doc.storage_path]);
      if (storageError) throw storageError;
      const { error: dbError } = await supabase.from("documents").delete().eq("id", doc.id);
      if (dbError) throw dbError;
      setMessage("Document deleted.");
      await loadDocuments();
    } catch (error) {
      setMessage(error?.message || "Unable to delete this document.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (doc) => {
    const url = `${PUBLIC_BASE_URL}/${doc.storage_path}`;
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Public document link copied.");
    } catch {
      setMessage(url);
    }
  };

  return <>
    <Header session={session} profile={profile} />
    <main className="nhsuk-main-wrapper ruht-documents-page" role="main">
      <div className="nhsuk-width-container">
        <header className="ruht-documents-hero">
          <div>
            <span className="pguh-eyebrow">Management</span>
            <h1>File Upload</h1>
            <p>Upload public PDF documents and create clean links on <strong>ruht.cloud</strong>.</p>
          </div>
          <div className="ruht-documents-hero__badge"><FaFilePdf /><span>PDF only</span></div>
        </header>

        {message && <div className="nhsuk-inset-text" role="status">{message}</div>}

        <section className="ruht-documents-upload-card">
          <div className="ruht-documents-section-heading">
            <div><span className="pguh-eyebrow">New document</span><h2>Upload a PDF</h2><p>The file name becomes the permanent public link.</p></div>
          </div>
          <form onSubmit={upload} className="ruht-documents-form">
            <label><span>File name</span><small>Letters, numbers, hyphens and underscores. “.pdf” is added automatically.</small><div className="ruht-document-url-input"><span>ruht.cloud/documents/</span><input className="nhsuk-input" value={fileName} onChange={(e)=>setFileName(cleanSlug(e.target.value))} placeholder="letter" maxLength="120" required/><strong>.pdf</strong></div></label>
            <label><span>Display name <small>(optional)</small></span><small>Shown in the document register. Leave blank to use the file name.</small><input className="nhsuk-input" value={displayName} onChange={(e)=>setDisplayName(e.target.value)} placeholder="Employment confirmation letter" /></label>
            <div className="ruht-document-upload-field">
              <div className="ruht-document-upload-field__label">Upload document</div>
              <label className={`ruht-document-file-picker${file ? " has-file" : ""}`} htmlFor="ruht-document-file">
                <input id="ruht-document-file" className="ruht-document-file-input" type="file" accept="application/pdf,.pdf" onChange={(e)=>setFile(e.target.files?.[0] || null)} required/>
                <span className="ruht-document-file-picker__button"><FaFilePdf /> Choose PDF</span>
                <span className="ruht-document-file-picker__name">{file ? file.name : "No PDF selected"}</span>
                <span className="ruht-document-file-picker__meta">{file ? formatBytes(file.size) : "Maximum file size 10 MB"}</span>
              </label>
              <small>PDF files only. The selected document will be publicly accessible using the generated ruht.cloud link.</small>
            </div>
            <div className="ruht-documents-form__actions"><button className="nhsuk-button" type="submit" disabled={busy}><FaUpload /> {busy ? "Uploading…" : "Upload document"}</button></div>
          </form>
        </section>

        <section className="ruht-documents-register">
          <div className="ruht-documents-section-heading ruht-documents-section-heading--register">
            <div><span className="pguh-eyebrow">Document register</span><h2>Uploaded documents</h2><p>{documents.length} active public document{documents.length === 1 ? "" : "s"}.</p></div>
            <label><span className="nhsuk-u-visually-hidden">Search documents</span><input className="nhsuk-input" value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Search documents" /></label>
          </div>

          <div className="ruht-document-list">
            {visible.map((doc) => {
              const url = `${PUBLIC_BASE_URL}/${doc.storage_path}`;
              return <article className="ruht-document-row" key={doc.id}>
                <div className="ruht-document-row__icon"><FaFilePdf /></div>
                <div className="ruht-document-row__main">
                  <strong>{doc.display_name || doc.file_name}</strong>
                  <a href={url} target="_blank" rel="noreferrer">{url}</a>
                  <small>{formatBytes(doc.file_size)} · Uploaded {new Date(doc.uploaded_at).toLocaleString("en-GB")}{doc.profiles ? ` by ${formatProfileName(doc.profiles)}` : ""}</small>
                </div>
                <div className="ruht-document-row__actions">
                  <button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={()=>copyLink(doc)}><FaCopy /> Copy link</button>
                  <a className="nhsuk-button nhsuk-button--secondary" href={url} target="_blank" rel="noreferrer"><FaExternalLinkAlt /> Open</a>
                  <button type="button" className="ruht-document-delete" onClick={()=>removeDocument(doc)} disabled={busy}><FaTrash /> Delete</button>
                </div>
              </article>;
            })}
            {!visible.length && <div className="ruht-documents-empty"><FaFilePdf /><strong>No documents found</strong><p>Upload the first PDF or change your search.</p></div>}
          </div>
        </section>
      </div>
    </main>
  </>;
}
